import React, { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

/**
 * PracticeExercise — light drag-drop matching widget.
 * Supports HTML5 drag on desktop AND tap-to-select on mobile via the same
 * `selected` state (tap an item then tap a bucket).
 */
export default function PracticeExercise({ config }) {
  // placements: { itemId: groupKey | null (still in tray) }
  const initial = useMemo(() => {
    const m = {};
    config.items.forEach(i => { m[i.id] = null; });
    return m;
  }, [config]);

  const [placements, setPlacements] = useState(initial);
  const [selected, setSelected] = useState(null);   // tap-to-move selection
  const [checked, setChecked] = useState(false);

  const move = (itemId, groupKey) => {
    setPlacements(p => ({ ...p, [itemId]: groupKey }));
    setSelected(null);
    setChecked(false);
  };

  const trayItems = config.items.filter(i => placements[i.id] === null);
  const itemsIn = (key) => config.items.filter(i => placements[i.id] === key);

  const allPlaced = Object.values(placements).every(v => v !== null);
  const correctCount = config.items.filter(i => placements[i.id] === i.group).length;
  const perfect = correctCount === config.items.length;

  const reset = () => {
    setPlacements(initial);
    setSelected(null);
    setChecked(false);
  };

  const onDragStart = (e, itemId) => { e.dataTransfer.setData('text/plain', itemId); };
  const onDrop = (e, groupKey) => {
    e.preventDefault();
    const itemId = e.dataTransfer.getData('text/plain');
    if (itemId) move(itemId, groupKey);
  };
  const onDropTray = (e) => {
    e.preventDefault();
    const itemId = e.dataTransfer.getData('text/plain');
    if (itemId) move(itemId, null);
  };
  const allowDrop = (e) => e.preventDefault();

  return (
    <Card className="card-shadow" data-testid="practice-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          🧩 Practice
        </CardTitle>
        <p className="text-sm text-muted-foreground">{config.prompt}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tray */}
        <div
          className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-3 min-h-[60px] flex flex-wrap gap-2"
          data-testid="practice-tray"
          onDrop={onDropTray}
          onDragOver={allowDrop}
        >
          {trayItems.length === 0 && (
            <span className="text-xs italic text-muted-foreground self-center">
              All items placed — press <strong>Check</strong>.
            </span>
          )}
          {trayItems.map(it => (
            <button
              key={it.id}
              type="button"
              draggable
              onDragStart={(e) => onDragStart(e, it.id)}
              onClick={() => setSelected(s => (s === it.id ? null : it.id))}
              data-testid={`practice-item-${it.id}`}
              className={`px-3 py-1.5 rounded-md border text-sm bg-white transition-transform ${
                selected === it.id
                  ? 'border-primary ring-2 ring-primary/40 scale-105'
                  : 'border-slate-300 hover:border-primary/60 hover:shadow-sm'
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>

        {/* Drop groups */}
        <div className={`grid gap-3 ${config.groups.length > 2 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {config.groups.map(g => (
            <div
              key={g.key}
              data-testid={`practice-bucket-${g.key}`}
              onDrop={(e) => onDrop(e, g.key)}
              onDragOver={allowDrop}
              onClick={() => { if (selected) move(selected, g.key); }}
              className={`rounded-lg border-2 p-3 min-h-[100px] transition-colors ${
                selected
                  ? 'border-primary/60 bg-primary/5 cursor-pointer'
                  : 'border-slate-200 bg-white'
              }`}
            >
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
                {g.label}
              </div>
              <div className="flex flex-wrap gap-2">
                {itemsIn(g.key).map(it => {
                  const wrong = checked && it.group !== g.key;
                  const ok = checked && it.group === g.key;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      draggable
                      onDragStart={(e) => onDragStart(e, it.id)}
                      data-testid={`practice-placed-${it.id}`}
                      className={`px-3 py-1.5 rounded-md border text-sm ${
                        ok ? 'bg-emerald-100 border-emerald-400 text-emerald-900' :
                        wrong ? 'bg-red-100 border-red-400 text-red-900' :
                        'bg-white border-slate-300'
                      }`}
                    >
                      {it.label}{ok && ' ✓'}{wrong && ' ✗'}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Button
            size="sm"
            onClick={() => setChecked(true)}
            disabled={!allPlaced}
            data-testid="practice-check-btn"
          >
            Check my answers
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={reset}
            data-testid="practice-reset-btn"
          >
            Reset
          </Button>
          {checked && (
            <span
              data-testid="practice-feedback"
              className={`text-sm font-semibold ${perfect ? 'text-emerald-700' : 'text-amber-700'}`}
            >
              {perfect
                ? `🎉 ${config.feedback_ok}`
                : `${correctCount} / ${config.items.length} correct — ${config.feedback_partial}`}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
